import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { formValueSelector } from 'redux-form';
import { useLocation } from 'react-router';
import {
  fetchStudentEdit,
  REQUEST_STUDENT_EDIT,
  ROLE,
  REQUEST_USER_INFO,
  fetchUserInfo,
} from '@/modules/User';
import Centrify from 'sama_global/components/Centrify';
import { Box } from 'UIKit/Grid';
import Panel from 'sama_global/components/Panel';
import { useSnackbarListener } from 'sama_global/utils/hooks/snackbar';
import Form from 'sama_global/components/Form';
import {
  required,
  minLength4,
  validDate,
  universalPhoneNumber,
} from '@/utils/Validate';
import RenderDateField from 'sama_global/components/RenderInput/RenderDateField';
import RenderSelectField from 'sama_global/components/RenderInput/RenderSelectField';
import {
  fetchParticipantEdit,
  REQUEST_PARTICIPANT_EDIT,
  REQUEST_GET_PARTICIPANT,
  fetchParticipant,
} from '@/modules/Olimpic';
import {
  countries,
  useCategories,
  fetchDelegates,
  REQUEST_GET_DELEGATES,
} from 'sama_global/modules/olimpic';
import { isAdmin } from 'sama_global/utils/protect';
import { cleaRequest } from '@/modules/Requests';

const selector = formValueSelector('olimpic_participants_edit');

/* eslint-disable camelcase */

const Register = ({
  editUser,
  getUser,
  requests,
  editParticipant,
  birthday: birth,
  loadParticipant,
  user,
  olimpic,
  loadDelegates,
  clearRequest,
}) => {
  const schoolsRequest = requests[REQUEST_GET_DELEGATES];
  const creator = requests[REQUEST_USER_INFO]?.data;

  const delegates = useMemo(
    () =>
      [creator, ...(schoolsRequest?.data || [])]
        .filter(i => i)
        .map(delegate => ({
          value: delegate.id,
          label: `${delegate.name_en} ${delegate.surname_en}`,
        })),
    [schoolsRequest, creator]
  );

  const fields = [
    {
      name: 'email',
      locale: 'EMAIL',
      disabled: true,
    },
    {
      name: 'password',
      locale: 'PASSWORD',
      validate: [minLength4],
    },
    {
      name: 'name_en',
      locale: 'NAME',
      validate: [required],
    },
    {
      name: 'surname_en',
      locale: 'SURNAME',
      validate: [required],
    },
    {
      name: 'birthday',
      locale: 'DATEBIRTH',
      validate: [validDate],
      component: RenderDateField,
    },
    {
      name: 'phone',
      locale: 'PHONE',
      validate: [universalPhoneNumber],
    },
    {
      name: 'category',
      locale: 'SELECT_CATEGORY',
      validate: [required],
      component: RenderSelectField,
      options: useCategories(
        isAdmin({ user }) ? user : { birthday: birth, role: ROLE.LEARNER }
      ),
    },
    {
      name: 'country',
      locale: 'SELECT_COUNTRY',
      validate: [required],
      component: RenderSelectField,
      options: countries,
    },
  ];

  if (isAdmin({ user }))
    fields.unshift({
      name: 'creator_id',
      locale: 'DELEGATE',
      validate: [required],
      options: delegates || [],
      component: RenderSelectField,
    });

  const request = requests[REQUEST_PARTICIPANT_EDIT];
  const requestUser = requests[REQUEST_STUDENT_EDIT];
  const requestParticipant = requests[REQUEST_GET_PARTICIPANT];

  const participant = useMemo(() => {
    const data = requestParticipant?.data;

    if (!data) return {};

    const {
      name_en,
      surname_en,
      birthday,
      email,
      password,
      phone,
      id,
      creator_id,
    } = data.user;
    const { category, country } = data;
    return {
      creator_id,
      userid: id,
      name_en,
      surname_en,
      birthday,
      email,
      password,
      phone,
      category,
      country,
    };
  }, [requestParticipant]);

  const participantId = useLocation().state?._id;

  useEffect(() => loadParticipant(participantId), [participantId]);

  useEffect(() => {
    if (participant?.creator_id) getUser(participant.creator_id);
  }, [participant]);

  useEffect(() => {
    if (olimpic?._id) loadDelegates(olimpic?._id);
  }, [olimpic]);

  useEffect(
    () => () => {
      clearRequest(REQUEST_PARTICIPANT_EDIT);
      clearRequest(REQUEST_STUDENT_EDIT);
      clearRequest(REQUEST_GET_PARTICIPANT);
    },
    []
  );

  const handleSubmit = async formData => {
    const {
      creator_id,
      name_en,
      surname_en,
      birthday,
      email,
      password,
      phone,
      category,
      country,
    } = formData;

    editUser(participant.userid, {
      creator_id: { value: creator_id },
      name_en,
      surname_en,
      birthday,
      email,
      password,
      phone,
    });

    editParticipant(participantId, {
      category: { value: category },
      country: { value: country },
    });
  };

  useSnackbarListener(
    request?.success && requestUser?.success ? 'SAVED' : '',
    'success'
  );

  useSnackbarListener(requestUser?.errorMessage);
  useSnackbarListener(request?.errorMessage);

  return (
    <Centrify>
      <Box py={5} w={1} style={{ maxWidth: '620px', margin: 'auto' }}>
        <Panel>
          {requestParticipant?.data && schoolsRequest?.data && (
            <Form
              title="EDIT_PARTICIPANT"
              fields={fields}
              initialValues={participant}
              onSubmit={handleSubmit}
              form="olimpic_participants_edit"
              fit
            />
          )}
        </Panel>
      </Box>
    </Centrify>
  );
};

Register.propTypes = {
  requests: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  olimpic: PropTypes.object.isRequired,
  editUser: PropTypes.func.isRequired,
  getUser: PropTypes.func.isRequired,
  loadParticipant: PropTypes.func.isRequired,
  editParticipant: PropTypes.func.isRequired,
  birthday: PropTypes.string.isRequired,
  loadDelegates: PropTypes.func.isRequired,
  clearRequest: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  requests: state.requests,
  user: state.user,
  olimpic: state.olimpic.sama,
  birthday: selector(state, 'birthday'),
});

const mapDispatchToProps = dispatch => ({
  loadParticipant(id) {
    dispatch(fetchParticipant(id));
  },
  loadDelegates(id) {
    dispatch(fetchDelegates(id));
  },
  editUser(id, formData) {
    dispatch(fetchStudentEdit(id, formData));
  },
  getUser(id) {
    dispatch(fetchUserInfo(id));
  },
  editParticipant(id, formData) {
    dispatch(fetchParticipantEdit(id, formData));
  },
  clearRequest(name) {
    dispatch(cleaRequest(name));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Register);
